import React, { useEffect, useRef } from 'react';
import Eyebrow from './Eyebrow';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
  isLoading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  isLoading = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus the cancel button when modal opens
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const cancelButton = modalRef.current.querySelector('[data-cancel-button]') as HTMLElement;
      if (cancelButton) {
        cancelButton.focus();
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const getConfirmButtonClass = () => {
    switch (variant) {
      case 'danger':
        return 'btn-destructive';
      case 'warning':
        return 'bg-caution-700 hover:opacity-90 text-white px-4 py-2 rounded-md font-medium transition-opacity';
      default:
        return 'btn-primary';
    }
  };

  // Flat accent line — gradients are retired.
  const getAccentColor = () => {
    switch (variant) {
      case 'danger':
        return 'var(--claret-700)';
      case 'warning':
        return 'var(--caution-700)';
      default:
        return 'var(--navy-700)';
    }
  };

  const getVariantLabel = () => {
    switch (variant) {
      case 'danger':
        return 'Destructive Action';
      case 'warning':
        return 'Please Confirm';
      default:
        return 'Confirmation';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px] transition-opacity"
        onClick={isLoading ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div
        ref={modalRef}
        className="relative bg-background rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all overflow-hidden border border-border"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Accent line */}
        <div className="h-1" style={{ background: getAccentColor() }} />

        <div className="px-6 pt-5 pb-6">
          {/* Variant micro-label */}
          <Eyebrow>{getVariantLabel()}</Eyebrow>

          {/* Title */}
          <h3 id="modal-title" className="font-serif text-[20px] font-medium text-foreground mt-2">
            {title}
          </h3>

          {/* Message */}
          <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
            {message}
          </div>

          {/* Divider */}
          <div className="h-px bg-stone-200 mt-6 mb-5" />

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              data-cancel-button
              className="btn-outline"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className={getConfirmButtonClass()}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
